import {useState} from 'react'
import {css} from '@emotion/react'
import {colors} from '../../../constants'

const translations = {
    en: {
        section1: {
            title: `I already have an adblocker. Why should I use this extension?`,
            description: `If you’re using other privacy-oriented tools while you browse, you can still benefit from having the Startpage Privacy Protection extension. Here’s why: different privacy tools find and block intrusive trackers in different ways. Our extension can overlap and block some of the same content that your other tools do, but if the technology behind each application is different there will always be trackers one catches while the other cannot.`,
        },
        section2: {
            title: `What is Startpage?`,
            description: `Startpage is the world’s most private search engine. We were founded in 2006, in the Netherlands, and since then we have provided best-in-class search results without collecting any personal information. We believe anyone in the world has the right to access and search the internet with true privacy.`,
        },
        section3: {
            title: `Is my data still safe with this extension?`,
            description: `Yes! We don’t collect any personal information. Anonymous privacy score data is stored locally in your browser and can be deleted at any time.`,
        },
        section4: {
            title: `What is the Privacy Score and how to you calculate it?`,
            description: `Privacy Score measures a site’s risk to your personal information, with 1 posing the most risk, and 5 the least. We use Privacy Badger's database of known trackers and also consider a few additional factors, such as whether or not a site uses HTTPS and whether a tracker is owned by one of the major tracking networks.`,
        },
        section5: {
            title: `Can I allowlist any sites or apps?`,
            description: `Yes! The Startpage Privacy Extension is designed to work right out of the box, but may from time to time affect a site’s ability to fully function. To help, we included support for allowing specific cookies, apps, and other widgets, as well as the ability to turn off or on for a specific site.`,
        },
    },
    de: {
        section1: {
            title: `Ich habe bereits einen Adblocker. Wofür benötige ich diese Erweiterung?`,
            description: `Wenn du beim Surfen andere datenschutzorientierte Tools verwendest, kannst du trotzdem von der Startpage Privatsphäre-Schutz Erweiterung profitieren. Denn verschiedene Datenschutz-Tools finden und blockieren aufdringliche Tracker auf unterschiedliche Weise. Unsere Erweiterung kann sich zwar mit anderen Tools überschneiden und einige der gleichen Inhalte blockieren, aber weil die Technologie hinter jeder Anwendung unterschiedlich ist, wird es immer Tracker geben, die das eine Tool abfängt und das andere nicht.`,
        },
        section2: {
            title: `Was ist Startpage?`,
            description: `Startpage ist die sicherste Suchmaschine der Welt. Seit unserer Gründung 2006 in den Niederlanden bieten wir erstklassige Suchergebnisse, ohne personenbezogene Daten zu erfassen. Wir sind der Meinung, dass jeder auf der Welt das Recht hat, wirklich geschützt auf das Internet zuzugreifen und im Internet zu suchen.`,
        },
        section3: {
            title: `Sind meine Daten mit dieser Erweiterung immer noch sicher?`,
            description: `Ja! Wir sammeln keine persönlichen Daten. Die Daten aus der Datenschutzbewertung bleiben anonym und werden nur lokal in deinem Browser gespeichert. Du kannst sie jederzeit löschen.`,
        },
        section4: {
            title: `Was ist die Datenschutzbewertung und wie wird sie berechnet?`,
            description: `Die Datenschutzbewertung misst, inwiefern eine Website ein Risiko für deine personenbezogenen Daten darstellt. Eine 1 bedeutet dabei das höchste Risiko, eine 5 das geringste. Wir verwenden die Datenbank bekannter Tracker von Privacy Badger und berücksichtigen zudem einige weitere Faktoren, beispielsweise, ob eine Website HTTPS verwendet und ob ein Tracker einem der großen Tracking-Netzwerke angehört.`,
        },
        section5: {
            title: `Kann ich eine Liste mit Websites oder Apps erstellen, die ich zulassen möchte?`,
            description: `Ja! Die Datenschutzerweiterung von Startpage ist direkt einsatzbereit, gelegentlich kann es jedoch vorkommen, dass sie die volle Funktionsfähigkeit einer Website beeinträchtigt. Zur Abhilfe bieten wir die Möglichkeit, bestimmte Cookies, Apps und andere Widgets zuzulassen sowie eine bestimmte Website zu deaktivieren oder zu aktivieren.`,
        },
    },
}

const FaqWidget = ({languageCode = `en`}) => {
    const text = translations[languageCode]
    const sections = Object.keys(text)

    const [expanded, setIsExpanded] = useState(new Array(sections.length).fill(false))

    const toggleSectionExpanded = (index) => {
        return (e) => {
            const newExpanded = [...expanded]
            newExpanded[index] = !newExpanded[index]
            setIsExpanded(newExpanded)
        }
    }

    return (
        <div
            className="faq-widget"
            css={css`
                background: ${colors.whisperLight};
                border-radius: 4px;
                padding: 24px;
                color: ${colors.midnight};
                box-shadow: 0px 10px 20px rgba(32, 44, 70, 0.05);
            `}
        >
            {sections.map((key, i) => {
                const {title, description} = text[key]
                const isExpanded = expanded[i]
                return (
                    <div
                        className="faq"
                        key={title}
                        css={css`
                            display: flex;
                            justify-content: space-between;
                            padding: 0 0 24px;
                            margin: 0 0 24px;
                            border-bottom: 1px solid ${colors.whisperDarkest};
                            cursor: pointer;
                            &:nth-last-of-type(1) {
                                border-bottom: none;
                                padding: 0;
                                margin: 0;
                            }
                        `}
                        role="button"
                        tabIndex={-1}
                        onClick={toggleSectionExpanded(i)}
                        onKeyUp={toggleSectionExpanded(i)}
                    >
                        <div className="faq-text">
                            <h4
                                className="faq-title"
                                css={css`
                                    margin: 0 0 ${isExpanded && '12px'};
                                    font-weight: 600;
                                    color: ${isExpanded && `${colors.brandBlue}`} !important;
                                    font-size: 18px;
                                `}
                            >
                                {title}
                            </h4>
                            <p
                                className="faq-descriotion"
                                css={css`
                                    line-height: 24px;
                                    display: ${isExpanded ? 'block' : 'none'};
                                `}
                            >
                                {description}
                            </p>
                        </div>
                        <button
                            type="button"
                            css={css`
                                background: transparent;
                                border: none;
                                height: 20px;
                                padding: 0;
                                margin: 0 10px 0 0;
                                cursor: pointer;
                                svg {
                                    transform: ${isExpanded ? 'rotate(0deg)' : 'rotate(180deg)'};
                                }
                            `}
                        >
                            <svg
                                width="14"
                                height="9"
                                viewBox="0 0 14 9"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M1 8L7 2L13 8" stroke="#6677FB" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        </button>
                    </div>
                )
            })}
        </div>
    )
}

export default FaqWidget
